import { Box, CircularProgress, Typography } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { usePagesTranslation } from "../../../../pages/locales";
import {
    beginPlayFromSlidingId,
    fetchMyPuzzlesPage,
    normalizePlaySession,
    playNavigateState,
    sessionDurationCaption,
    sessionModeLine,
    sessionToLibraryGroup,
} from "../lib/api";
import LibraryCard from "../components/LibraryCard";
import MyPuzzlesEmptyState from "../components/MyPuzzlesEmptyState";
import PageShell from "../components/PageShell";
import SegmentedControl from "../components/SegmentedControl";
import {
    getSlidingStoryTitle,
    pickSlidingOriginalImageUrl,
    pickSlidingPlayImageUrl,
} from "../../../../pages/browse-sliding/slidingBrowseUtils";
import { captionSx } from "../styles/mainStyles";
import { useSlidingAppearance } from "../lib/AppearanceContext";
import { slidingPageContentSx } from "../lib/appearance";
import { attachPlayReturnPath, navigateToPlay } from "../lib/playRouteState";
import {
    chargePremiumPlayCreditOnEntry,
    isInsufficientCreditError,
    resolvePremiumPlayCreditContext,
} from "../lib/slidingCreditConsume";
import Logger from "../../../../services/Logger";
import { MY_PUZZLES_PATH, PLAY_PATH, SETTINGS_PATH } from "../routes";
import { buildLocalPlayNavigateState } from "../lib/access";
import AuthService from "../../../../services/AuthService";
import { useSubscription } from "../lib/SubscriptionContext";

const PAGE_SIZE = 12;

const TAB_PROGRESS = "progress";
const TAB_COMPLETE = "complete";

export default function MyPuzzlesPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = usePagesTranslation();
    const { tokens } = useSlidingAppearance();
    const { access, isSubscribed, isConfigurationLocked, credits } = useSubscription();
    const [tab, setTab] = useState(TAB_PROGRESS);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [startingKey, setStartingKey] = useState(null);
    const [error, setError] = useState(null);

    const tabOptions = [
        {
            value: TAB_PROGRESS,
            label: t("native.myPuzzles.inProgress", { defaultValue: "In Progress" }),
        },
        {
            value: TAB_COMPLETE,
            label: t("native.myPuzzles.complete", { defaultValue: "Complete" }),
        },
    ];

    const load = useCallback(
        async (tabId, { silent = false } = {}) => {
            const isCompleted = tabId === TAB_COMPLETE;
            if (!silent) {
                setLoading(true);
            }
            setError(null);
            try {
                const paged = await fetchMyPuzzlesPage({
                    page: 0,
                    pageSize: PAGE_SIZE,
                    isCompleted,
                    onlyStarted: true,
                });
                const content = (paged.content || [])
                    .map((row) => normalizePlaySession(row))
                    .filter(Boolean);
                setRows(content);
            } catch {
                setError(
                    t("native.myPuzzles.loadError", {
                        defaultValue: "Could not load your puzzles. Sign in to continue.",
                    })
                );
                setRows([]);
            } finally {
                if (!silent) {
                    setLoading(false);
                }
            }
        },
        [t]
    );

    const locationKeyRef = useRef(location.key);

    useEffect(() => {
        load(tab, { silent: false });
    }, [tab, load]);

    useEffect(() => {
        if (locationKeyRef.current === location.key) {
            return;
        }
        locationKeyRef.current = location.key;
        void load(tab, { silent: true });
    }, [location.key, tab, load]);

    const startPlay = async (session, { slidingId, gridSize, difficulty }) => {
        const key = session.id || session.slidingId;
        setStartingKey(key);
        setError(null);
        try {
            if (isConfigurationLocked(gridSize, difficulty)) {
                navigate(SETTINGS_PATH);
                return;
            }

            const resumeSame =
                tab === TAB_PROGRESS &&
                !session.isCompleted &&
                session.slidingId === slidingId &&
                session.gridSize === gridSize &&
                session.difficulty === difficulty &&
                Array.isArray(session.layout);

            const authed = await AuthService.isAuthenticated();

            if (resumeSame) {
                if (!authed) {
                    navigateToPlay(
                        navigate,
                        PLAY_PATH,
                        attachPlayReturnPath(
                            buildLocalPlayNavigateState({
                                gridSize,
                                difficulty,
                                story: session.story,
                                storyTitle: session.storyTitle || getSlidingStoryTitle(session.story),
                                imageUrl: pickSlidingPlayImageUrl(session.story) || session.imageUrl,
                                previewImageUrl:
                                    session.previewImageUrl ?? pickSlidingOriginalImageUrl(session.story),
                                layout: session.layout,
                                resume: true,
                            }),
                            MY_PUZZLES_PATH,
                        ),
                    );
                    return;
                }
                const state = playNavigateState(session, { resume: true });
                if (state) {
                    navigateToPlay(
                        navigate,
                        PLAY_PATH,
                        attachPlayReturnPath(state, MY_PUZZLES_PATH),
                    );
                    return;
                }
            }

            if (!authed) {
                navigateToPlay(
                    navigate,
                    PLAY_PATH,
                    attachPlayReturnPath(
                        buildLocalPlayNavigateState({
                            gridSize,
                            difficulty,
                            story: session.story,
                            storyTitle: session.storyTitle || getSlidingStoryTitle(session.story),
                            imageUrl: pickSlidingPlayImageUrl(session.story) || session.imageUrl,
                            previewImageUrl:
                                session.previewImageUrl ?? pickSlidingOriginalImageUrl(session.story),
                        }),
                        MY_PUZZLES_PATH,
                    ),
                );
                return;
            }

            const result = await beginPlayFromSlidingId(slidingId, {
                gridSize,
                difficulty,
                imageUrl: pickSlidingPlayImageUrl(session.story) || session.imageUrl,
                story: session.story,
                storyTitle: session.storyTitle || getSlidingStoryTitle(session.story),
                previewImageUrl:
                    session.previewImageUrl ?? pickSlidingOriginalImageUrl(session.story),
                returnPath: MY_PUZZLES_PATH,
            });
            if (result.error) {
                setError(result.error);
            }
            const playState =
                result.state ||
                {
                    slidingId,
                    gridSize,
                    difficulty,
                    imageUrl: pickSlidingPlayImageUrl(session.story) || session.imageUrl,
                    storyTitle: session.storyTitle || getSlidingStoryTitle(session.story),
                    previewImageUrl:
                        session.previewImageUrl ?? pickSlidingOriginalImageUrl(session.story),
                    layout: session.layout,
                };
            if (playState?.slidingId) {
                try {
                    const creditCtx = await resolvePremiumPlayCreditContext({ access, credits });
                    await chargePremiumPlayCreditOnEntry({
                        slidingId: playState.slidingId,
                        gridSize,
                        difficulty,
                        hasLegacyUnlimitedAccess: creditCtx.hasLegacyUnlimitedAccess,
                        hasCredits: creditCtx.hasCredits,
                        applyCreditBalance: credits.applyCreditBalance,
                    });
                } catch (e) {
                    if (isInsufficientCreditError(e)) {
                        navigate(SETTINGS_PATH);
                        return;
                    }
                    Logger.warn("MyPuzzlesPage.startPlay credit charge", e);
                }
            }
            navigateToPlay(
                navigate,
                PLAY_PATH,
                attachPlayReturnPath(playState, MY_PUZZLES_PATH),
            );
        } finally {
            setStartingKey(null);
        }
    };

    return (
        <PageShell
            showSettings
            showBrandHeader={false}
            pageHero="myPuzzles"
            pageTitle={t("native.myPuzzles.title", { defaultValue: "MY PUZZLES" })}
        >
            <Box
                sx={{
                    ...slidingPageContentSx(tokens),
                    bgcolor: "transparent",
                    background: "transparent",
                }}
            >
                <Box sx={{ mb: 2 }}>
                    <SegmentedControl
                        asTabs
                        aria-label={t("native.myPuzzles.tabList", { defaultValue: "My puzzles" })}
                        options={tabOptions}
                        value={tab}
                        onChange={setTab}
                        disabled={loading}
                    />
                </Box>

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress sx={{ color: "#3BA666" }} />
                    </Box>
                ) : null}

                {!loading && rows.length === 0 ? (
                    <MyPuzzlesEmptyState variant={tab === TAB_COMPLETE ? "complete" : "progress"} />
                ) : null}

                {!loading
                    ? rows.map((session) => {
                          const group = sessionToLibraryGroup(session);
                          const key = session.id || session.slidingId;
                          if (!group) return null;
                          return (
                              <LibraryCard
                                  key={key}
                                  group={group}
                                  thumbnailSize={200}
                                  initialGridSize={session.gridSize}
                                  initialDifficulty={session.difficulty}
                                  hidePlayButton={tab === TAB_COMPLETE}
                                  progressSimple={tab === TAB_PROGRESS}
                                  modeLine={tab === TAB_PROGRESS ? sessionModeLine(session) : null}
                                  playing={tab !== TAB_COMPLETE && startingKey === key}
                                  durationCaption={
                                      tab === TAB_COMPLETE ? sessionDurationCaption(session) : null
                                  }
                                  onPlay={(opts) => startPlay(session, opts)}
                              freeTier={!isSubscribed}
                          />
                      );
                  })
                : null}

            {error ? (
                    <Typography sx={{ ...captionSx, color: tokens.error, textAlign: "center", mt: 1 }}>
                        {error}
                    </Typography>
                ) : null}
            </Box>
        </PageShell>
    );
}
